import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { HustleBadge, type HustleLevel } from "@/components/HustleBadge";
import { formatMoney } from "@/lib/money";
import { ArrowDownLeft, ArrowUpRight, ChevronRight, Loader2, Sparkles, Trophy } from "lucide-react";

interface HistoryRow {
  id: string;
  old_score: number;
  new_score: number;
  delta: number;
  old_level: HustleLevel | string;
  new_level: HustleLevel | string;
  reason: string;
  counterpart_id: string | null;
  metadata: { tx_id?: string; amount_cents?: number; from?: string; to?: string } | null;
  created_at: string;
}

const REASON_LABEL: Record<string, string> = {
  transfer_sent: "Sent payment",
  transfer_received: "Received payment",
  tier_change: "Tier change",
  referral_qualified: "Referral joined",
  recalc: "Score recalculated",
  backfill: "Initial score",
};

const formatRelative = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const HustleHistorySection = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<HistoryRow[] | null>(null);
  const [counterparts, setCounterparts] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("hustle_score_history")
        .select("id, old_score, new_score, delta, old_level, new_level, reason, counterpart_id, metadata, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      if (cancelled) return;
      const list = (data as HistoryRow[] | null) ?? [];
      setRows(list);

      const ids = Array.from(new Set(list.map((r) => r.counterpart_id).filter(Boolean))) as string[];
      if (ids.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, cashtag")
          .in("user_id", ids);
        if (cancelled) return;
        const map: Record<string, string> = {};
        (profs ?? []).forEach((p: { user_id: string; cashtag: string }) => {
          map[p.user_id] = p.cashtag;
        });
        setCounterparts(map);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (rows === null) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  const visible = expanded ? rows : rows.slice(0, 5);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-4 w-4 text-accent" />
        <h3 className="font-display font-bold">Hustle Score history</h3>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">
          Your score history will appear here once you start sending or receiving payments.
        </p>
      ) : (
        <>
          <ul className="divide-y divide-border">
            {visible.map((r) => (
              <li key={r.id} className="py-3 flex items-start gap-3">
                <Icon reason={r.reason} delta={r.delta} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight flex items-center gap-2 flex-wrap">
                    {REASON_LABEL[r.reason] ?? r.reason}
                    {r.counterpart_id && counterparts[r.counterpart_id] && (
                      <span className="text-muted-foreground font-normal">
                        · ${counterparts[r.counterpart_id]}
                      </span>
                    )}
                    {r.metadata?.amount_cents !== undefined && r.reason !== "tier_change" && (
                      <span className="text-muted-foreground font-normal">
                        · {formatMoney(r.metadata.amount_cents)}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {formatRelative(r.created_at)} · score {r.old_score} → {r.new_score}
                  </p>
                  {r.reason === "tier_change" && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <HustleBadge level={r.old_level} size="sm" />
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <HustleBadge level={r.new_level} size="sm" />
                    </div>
                  )}
                </div>
                {r.delta !== 0 && (
                  <span
                    className={
                      "text-sm font-semibold tabular-nums shrink-0 " +
                      (r.delta > 0 ? "text-primary" : "text-destructive")
                    }
                  >
                    {r.delta > 0 ? "+" : ""}{r.delta}
                  </span>
                )}
              </li>
            ))}
          </ul>

          {rows.length > 5 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-3 w-full text-xs font-medium text-primary hover:underline"
            >
              {expanded ? "Show less" : `Show all (${rows.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const Icon = ({ reason, delta }: { reason: string; delta: number }) => {
  const base = "h-9 w-9 rounded-full grid place-items-center shrink-0";
  if (reason === "tier_change") {
    return (
      <div className={base + " bg-accent/20 text-accent-foreground"}>
        <Trophy className="h-4 w-4" />
      </div>
    );
  }
  if (reason === "transfer_sent") {
    return (
      <div className={base + " bg-primary/15 text-primary"}>
        <ArrowUpRight className="h-4 w-4" />
      </div>
    );
  }
  if (reason === "transfer_received") {
    return (
      <div className={base + " bg-secondary text-secondary-foreground"}>
        <ArrowDownLeft className="h-4 w-4" />
      </div>
    );
  }
  return (
    <div className={base + " bg-muted text-muted-foreground"}>
      <Sparkles className="h-4 w-4" />
    </div>
  );
};
